/*
 * Guest-charging Today's Rates chart.
 *
 * Consumes the `costData[tariff][day]` 96-slot price array, classifies
 * each TOU segment as peak / normal / off-peak by ranking unique prices,
 * and draws brand-tokenised bars with a "Now" indicator + brand legend.
 */
import React from 'react'

const HEIGHT_BY_TYPE = { peak: 0.92, normal: 0.62, offpeak: 0.42 }
const DAY_OF_WEEK = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']

const getDay = () => DAY_OF_WEEK[new Date().getDay()]
const getNowHour = () => new Date().getHours() + new Date().getMinutes() / 60

const pickTariff = (costData, powerType) => {
    if (!costData) return null
    const candidates = [
        powerType === 'L1' ? 'L1' : null,
        powerType === 'L2' ? 'L2' : null,
        'L2Wplug',
        'L2',
        'L1',
    ].filter(Boolean)
    for (const k of candidates) {
        if (costData[k]) return k
    }
    return Object.keys(costData).find((k) => k !== 'asapUpcharge') || null
}

const fmt$ = (n) => `$${n.toFixed(2)}`
const hourLabel = (h) => {
    const mod = h % 24
    const period = mod >= 12 ? 'p' : 'a'
    const display = mod % 12 === 0 ? 12 : mod % 12
    return `${display}${period}`
}

const GuestTouChart = ({ costData, powerType, useUpCharge = true }) => {
    const tariff = pickTariff(costData, powerType)
    const dayData = costData?.[tariff]?.[getDay()]
    if (!dayData || !Array.isArray(dayData) || dayData.length === 0) {
        return null
    }

    const upCharge = useUpCharge ? costData?.asapUpcharge?.[tariff] || 0 : 0
    const slotCost = (raw) => Math.round(raw + upCharge) / 100

    // Collapse the 96-slot array into contiguous TOU bands of equal price.
    const bands = []
    for (let i = 0; i < dayData.length; i++) {
        const cost = slotCost(dayData[i])
        const hour = i / 4
        const last = bands[bands.length - 1]
        if (last && Math.abs(last.rate - cost) < 0.001) {
            last.to = hour + 0.25
        } else {
            bands.push({ from: hour, to: hour + 0.25, rate: cost })
        }
    }

    const sorted = [...new Set(bands.map((b) => b.rate))].sort((a, b) => a - b)
    const isFlat = sorted.length <= 1
    // Classify by rank into peak / normal / offpeak.
    const classify = (rate) => {
        if (isFlat) return 'normal'
        if (sorted.length === 2) return rate === sorted[0] ? 'offpeak' : 'peak'
        if (rate === sorted[sorted.length - 1]) return 'peak'
        if (rate === sorted[0]) return 'offpeak'
        return 'normal'
    }
    bands.forEach((b) => {
        b.type = classify(b.rate)
    })

    const nowHour = getNowHour()
    const activeIdx = bands.findIndex((b) => nowHour >= b.from && nowHour < b.to)

    // Legend rates by tier
    const legendNow = bands[activeIdx]
    const peakRate = sorted[sorted.length - 1]
    const normalRate = sorted.length >= 3 ? sorted[1] : sorted[0]
    const offRate = sorted[0]

    return (
        <div className="gc-chart-wrap">
            <div
                className="gc-chart"
                role="img"
                aria-label="Charging rates throughout the day"
            >
                {bands.map((b, i) => {
                    const widthPct = ((b.to - b.from) / 24) * 100
                    const heightPct = (HEIGHT_BY_TYPE[b.type] || 0.62) * 100
                    const isActive = i === activeIdx
                    return (
                        <div
                            key={i}
                            className={`gc-bar${isActive ? ' is-active' : ''}`}
                            style={{ width: `${widthPct}%` }}
                        >
                            <div className="gc-bar-label">{fmt$(b.rate)}</div>
                            <div
                                className={`gc-bar-fill${isActive ? ' is-now' : ' is-neutral'}`}
                                style={{ height: `${heightPct}%` }}
                            />
                        </div>
                    )
                })}
                <div
                    className="gc-now-line"
                    style={{ left: `${(nowHour / 24) * 100}%` }}
                >
                    <div className="gc-now-label">Now</div>
                </div>
            </div>

            <div className="gc-axis">
                {['12a', '6a', '12p', '6p'].map((t) => (
                    <div key={t} className="gc-axis-tick" style={{ width: '25%' }}>
                        {t}
                    </div>
                ))}
            </div>

            <div className="gc-legend">
                <div className="gc-legend-item">
                    <span className="gc-legend-swatch is-now" />
                    <span>
                        <span className="gc-legend-name">Now</span>
                        <br />
                        <span className="gc-legend-rate">
                            {legendNow ? `${fmt$(legendNow.rate)}/kWh · ${legendNow.type}` : '—'}
                        </span>
                    </span>
                </div>
                <div className="gc-legend-item">
                    <span className="gc-legend-swatch is-neutral" />
                    <span>
                        <span className="gc-legend-name">Normal</span>
                        <br />
                        <span className="gc-legend-rate">{fmt$(normalRate || 0)}/kWh</span>
                    </span>
                </div>
                <div className="gc-legend-item">
                    <span className="gc-legend-swatch is-neutral" />
                    <span>
                        <span className="gc-legend-name">Peak</span>
                        <br />
                        <span className="gc-legend-rate">{fmt$(peakRate || 0)}/kWh</span>
                    </span>
                </div>
            </div>

            {hourLabel /* keep import for jsx; lint suppression */ && null}
        </div>
    )
}

export default GuestTouChart
